import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  Target,
  MousePointer2,
  CheckCircle2,
  XCircle,
  ChevronRight,
  Clock,
  HelpCircle,
  MapPin,
  Crosshair,
  AlertCircle,
  Trophy,
  Hand,
  RotateCcw,
  Navigation
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * InteractiveQuizOverlay - Handles interactive 3D quiz questions
 * User must click on correct objects in the 3D space to answer questions
 */

// Custom hotspot icon URLs
const HOTSPOT_ICONS = {
  correct: 'https://customer-assets.emergentagent.com/job_817bdca5-cb3c-4cda-bf89-74ce1f443d99/artifacts/cyu7xupi_hs_gray_1.png',
  equipment: 'https://customer-assets.emergentagent.com/job_817bdca5-cb3c-4cda-bf89-74ce1f443d99/artifacts/2mp799up_hs_gray_2.png',
  navigation: 'https://customer-assets.emergentagent.com/job_817bdca5-cb3c-4cda-bf89-74ce1f443d99/artifacts/cvver25m_hs_gray_3.png',
};

export function InteractiveQuizOverlay({
  isOpen,
  onClose,
  quiz,
  tourId,
  sdkInstance,
  pointerIntersection,
  onQuizComplete,
  onClickCapture, // New prop: callback when user clicks in 3D mode
  customCorrectIcon, // Optional custom icon URL for correct answers
  customWrongIcon, // Optional custom icon URL for wrong answers
  navigateToSweep, // Navigate to sweep location
}) {
  const { token } = useAuth();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [feedback, setFeedback] = useState(null); // { correct: boolean, message: string }
  const [isListening, setIsListening] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [result, setResult] = useState(null);
  const [hoveredObject, setHoveredObject] = useState(null);
  const [clickedPosition, setClickedPosition] = useState(null); // Store where user clicked for visual feedback
  const [isNavigating, setIsNavigating] = useState(false);

  // Keep a ref to the latest answers to avoid stale closure issues
  const answersRef = useRef({});
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  // Log when component mounts/updates
  console.log('InteractiveQuizOverlay render:', { isOpen, quizTitle: quiz?.title, navigateToSweepAvailable: !!navigateToSweep });

  // Current question
  const currentQuestion = quiz?.questions?.[currentQuestionIndex];
  // A question is interactive ONLY if explicitly set to interactive_click or location_click type
  // Multiple choice questions with locations still show MC options (just navigate there first)
  const isInteractiveQuestion = currentQuestion?.question_type === 'interactive_click' || 
                                currentQuestion?.question_type === 'location_click';

  // Track if we've already navigated for current question
  const hasNavigatedRef = useRef(false);
  const lastQuestionIdRef = useRef(null);

  // Reset navigation flag when question changes
  useEffect(() => {
    if (currentQuestion?.id !== lastQuestionIdRef.current) {
      hasNavigatedRef.current = false;
      lastQuestionIdRef.current = currentQuestion?.id;
    }
  }, [currentQuestion?.id]);

  // Navigate to question location when question changes
  useEffect(() => {
    // Skip if already navigated for this question
    if (hasNavigatedRef.current) {
      return;
    }
    
    const navigateToQuestionLocation = async () => {
      if (!currentQuestion || !navigateToSweep) {
        console.log('QUIZ NAV: Skipping - no question or no navigateToSweep');
        return;
      }
      
      const sweepId = currentQuestion.target_sweep_id;
      const targetRotation = currentQuestion.target_rotation; // Use saved rotation!
      
      console.log('==========================================');
      console.log('=== QUIZ NAVIGATION START ===');
      console.log('==========================================');
      console.log('Question title/text:', currentQuestion.question?.substring(0, 50));
      console.log('sweepId:', sweepId);
      console.log('targetRotation:', JSON.stringify(targetRotation));
      console.log('Has rotation data:', !!(targetRotation && (targetRotation.x !== undefined || targetRotation.y !== undefined)));
      
      if (!sweepId) {
        console.log('QUIZ NAV: No target_sweep_id for this question - skipping navigation');
        return;
      }
      
      // Mark as navigated BEFORE starting
      hasNavigatedRef.current = true;
      
      setIsNavigating(true);
      
      try {
        // Navigate to sweep WITH the saved rotation
        const navOptions = { 
          transition: 'fly', 
          transitionTime: 800  // Reduced from 1500ms for faster navigation
        };
        
        // Use the saved rotation directly - no calculation needed!
        if (targetRotation && (targetRotation.x !== undefined || targetRotation.y !== undefined)) {
          navOptions.rotation = {
            x: Number(targetRotation.x) || 0,
            y: Number(targetRotation.y) || 0
          };
          console.log('QUIZ NAV: Using saved camera rotation:', JSON.stringify(navOptions.rotation));
        } else {
          console.warn('QUIZ NAV: ⚠️ No target_rotation found for this question - camera will not rotate to specific angle');
        }
        
        console.log('QUIZ NAV: Calling navigateToSweep with full options:', JSON.stringify(navOptions));
        await navigateToSweep(sweepId, navOptions);
        console.log('QUIZ NAV: navigateToSweep call returned - check for rotation logs above');
        console.log('==========================================');
        console.log('=== QUIZ NAVIGATION END ===');
        console.log('==========================================');
      } catch (err) {
        console.error('QUIZ NAV: Navigation failed with error:', err);
        console.error('QUIZ NAV: Error details:', err.message);
      }
      
      setTimeout(() => {
        setIsNavigating(false);
      }, 200);  // Reduced from 500ms
    };
    
    if (isOpen && currentQuestion && !result) {
      navigateToQuestionLocation();
    }
  }, [currentQuestionIndex, isOpen, result, navigateToSweep, currentQuestion]);

  // Track hovered object from pointerIntersection
  useEffect(() => {
    if (isListening && pointerIntersection) {
      const objectName = pointerIntersection.object?.replace('intersectedobject.', '') || 
                         pointerIntersection.floorId || 
                         'Unknown area';
      setHoveredObject({
        name: objectName,
        position: pointerIntersection.position,
        normal: pointerIntersection.normal
      });
    }
  }, [isListening, pointerIntersection]);

  // Initialize quiz
  useEffect(() => {
    if (isOpen && quiz) {
      setCurrentQuestionIndex(0);
      setAnswers({});
      setFeedback(null);
      setResult(null);
      setStartTime(Date.now());
      setIsListening(false);
      setHoveredObject(null);
      
      if (quiz.time_limit_minutes) {
        setTimeRemaining(quiz.time_limit_minutes * 60);
      }
    }
  }, [isOpen, quiz?.id]);

  // Submit quiz to backend - defined early so it can be used in timer
  const submitQuizToBackend = useCallback(async () => {
    if (!token || !quiz) return;

    try {
      // Use the ref to get the LATEST answers (avoids stale closure)
      const currentAnswers = answersRef.current;
      
      // Convert answers to simple format for backend
      const simpleAnswers = {};
      console.log('=== SUBMITTING QUIZ ===');
      console.log('Raw answers from ref:', currentAnswers);
      
      Object.entries(currentAnswers).forEach(([qId, answer]) => {
        if (typeof answer === 'object' && answer.is_correct !== undefined) {
          // Interactive click answer
          simpleAnswers[qId] = answer.is_correct ? 'correct' : answer.clicked_object;
          console.log(`Question ${qId}: is_correct=${answer.is_correct}, sending="${simpleAnswers[qId]}"`);
        } else {
          simpleAnswers[qId] = answer;
          console.log(`Question ${qId}: direct answer="${answer}"`);
        }
      });
      
      console.log('Final answers being sent:', simpleAnswers);

      const res = await fetch(`${API_URL}/api/elearning/quizzes/${quiz.id}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ answers: simpleAnswers })
      });

      if (res.ok) {
        const data = await res.json();
        console.log('Quiz result from backend:', data);
        setResult(data);
        onQuizComplete?.(data);
      } else {
        console.error('Quiz submit failed:', await res.text());
      }
    } catch (err) {
      console.error('Failed to submit quiz:', err);
    }
  }, [token, quiz, onQuizComplete]);

  // Timer countdown
  useEffect(() => {
    if (!isOpen || timeRemaining === null || result) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          submitQuizToBackend();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, timeRemaining, result, submitQuizToBackend]);

  // Calculate distance between two 3D points
  const calculateDistance = (pos1, pos2) => {
    if (!pos1 || !pos2) return Infinity;
    const dx = (pos1.x || 0) - (pos2.x || 0);
    const dy = (pos1.y || 0) - (pos2.y || 0);
    const dz = (pos1.z || 0) - (pos2.z || 0);
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  };

  // Handle click on 3D object (called when user clicks in listening mode)
  const handleObjectClick = useCallback(() => {
    if (!isListening || !hoveredObject || !currentQuestion) return;
    
    let isCorrect = false;
    let feedbackMessage = '';
    let clickDistance = null;
    
    // Check if question has a target position (preferred method)
    if (currentQuestion.target_position) {
      const distance = calculateDistance(hoveredObject.position, currentQuestion.target_position);
      clickDistance = distance;
      const radius = currentQuestion.click_radius || 2.0; // Default 2 meters tolerance
      isCorrect = distance <= radius;
      
      if (isCorrect) {
        feedbackMessage = currentQuestion.explanation || 'Correct! Well done.';
      } else {
        feedbackMessage = `Not quite - you clicked ${distance.toFixed(1)}m away. Try clicking closer to the ${currentQuestion.correct_answer}.`;
      }
    } else {
      // Fallback to name-based matching (less reliable)
      const targetAnswer = currentQuestion.correct_answer?.toLowerCase().trim() || '';
      const clickedLabel = hoveredObject.name?.toLowerCase().trim() || '';
      
      // Check for match (flexible matching - partial match allowed)
      isCorrect = clickedLabel.includes(targetAnswer) || 
                  targetAnswer.includes(clickedLabel) ||
                  clickedLabel === targetAnswer ||
                  clickedLabel.split(/[\s_-]+/).some(word => word.length > 2 && targetAnswer.includes(word)) ||
                  targetAnswer.split(/[\s_-]+/).some(word => word.length > 2 && clickedLabel.includes(word));

      if (isCorrect) {
        feedbackMessage = currentQuestion.explanation || 'Correct! Well done.';
      } else {
        // Don't show confusing "model" text - just say it's not the right location
        feedbackMessage = `That's not the ${currentQuestion.correct_answer}. Keep looking and try again!`;
      }
    }

    // Show feedback
    // Store the clicked position for visual feedback
    setClickedPosition(hoveredObject.position);

    setFeedback({
      correct: isCorrect,
      message: feedbackMessage,
      distance: clickDistance
    });

    setIsListening(false);

    // ONLY advance to next question if CORRECT
    // If wrong, let user try again
    if (isCorrect) {
      // Record the correct answer
      setAnswers(prev => ({
        ...prev,
        [currentQuestion.id]: {
          clicked_object: hoveredObject.name,
          clicked_position: hoveredObject.position,
          is_correct: true
        }
      }));

      // Auto-advance after showing success feedback
      setTimeout(() => {
        setClickedPosition(null); // Clear the marker
        if (currentQuestionIndex < quiz.questions.length - 1) {
          setCurrentQuestionIndex(prev => prev + 1);
          setFeedback(null);
          setHoveredObject(null);
        } else {
          submitQuizToBackend();
        }
      }, 2000);
    }
    // If wrong, feedback stays visible and user can click "Try Again"
  }, [isListening, hoveredObject, currentQuestion, currentQuestionIndex, quiz?.questions?.length, submitQuizToBackend]);

  // Handle multiple choice answer
  const handleMultipleChoiceAnswer = (answer) => {
    const isCorrect = answer === currentQuestion.correct_answer;
    
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: answer
    }));

    setFeedback({
      correct: isCorrect,
      message: isCorrect 
        ? (currentQuestion.explanation || 'Correct!')
        : `Incorrect. The correct answer was "${currentQuestion.correct_answer}".`
    });

    setTimeout(() => {
      if (currentQuestionIndex < quiz.questions.length - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
        setFeedback(null);
      } else {
        submitQuizToBackend();
      }
    }, 2000);
  };

  // Handle true/false answer
  const handleTrueFalseAnswer = (answer) => {
    const isCorrect = answer.toLowerCase() === currentQuestion.correct_answer?.toLowerCase();
    
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: answer
    }));

    setFeedback({
      correct: isCorrect,
      message: isCorrect 
        ? (currentQuestion.explanation || 'Correct!')
        : `Incorrect. The correct answer was "${currentQuestion.correct_answer}".`
    });

    setTimeout(() => {
      if (currentQuestionIndex < quiz.questions.length - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
        setFeedback(null);
      } else {
        submitQuizToBackend();
      }
    }, 2000);
  };

  const formatTime = (seconds) => {
    if (!seconds && seconds !== 0) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate current score - handles all question types
  const correctCount = Object.entries(answers).filter(([questionId, answer]) => {
    // Find the question to check the correct answer
    const question = quiz?.questions?.find(q => q.id === questionId);
    if (!question) return false;
    
    // Interactive click questions store {is_correct: true/false}
    if (typeof answer === 'object' && answer.is_correct !== undefined) {
      return answer.is_correct;
    }
    
    // Multiple choice and true/false store the answer string directly
    // Compare with the correct_answer from the question
    const correctAnswer = question.correct_answer?.toString().toLowerCase().trim();
    const userAnswer = answer?.toString().toLowerCase().trim();
    return userAnswer === correctAnswer;
  }).length;

  if (!isOpen || !quiz) return null;

  // Results screen
  if (result) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <Card className="bg-slate-900/95 border-slate-700 max-w-md w-full mx-4 p-8 text-center">
          {result.passed ? (
            <>
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Trophy className="h-10 w-10 text-emerald-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Congratulations!</h2>
              <p className="text-slate-400 mb-6">You passed the quiz!</p>
            </>
          ) : (
            <>
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                <XCircle className="h-10 w-10 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Keep Trying!</h2>
              <p className="text-slate-400 mb-6">You did not pass this time.</p>
            </>
          )}

          <div className="bg-slate-800/50 rounded-xl p-6 mb-6">
            <div className="text-4xl font-bold text-white mb-2">{result.score?.toFixed(0) || 0}%</div>
            <div className="text-sm text-slate-400">
              Passing Score: {quiz.passing_score}%
            </div>
            {result.time_taken_seconds && (
              <div className="text-sm text-slate-400 mt-2">
                Time: {formatTime(result.time_taken_seconds)}
              </div>
            )}
          </div>

          <div className="flex gap-3">
            {!result.passed && quiz.allow_retakes && (
              <Button 
                onClick={() => {
                  setCurrentQuestionIndex(0);
                  setAnswers({});
                  setFeedback(null);
                  setResult(null);
                  setStartTime(Date.now());
                  setIsListening(false);
                  setHoveredObject(null);
                  if (quiz.time_limit_minutes) {
                    setTimeRemaining(quiz.time_limit_minutes * 60);
                  }
                }}
                className="flex-1 bg-emerald-600 hover:bg-emerald-500"
              >
                Try Again
              </Button>
            )}
            <Button 
              onClick={onClose}
              variant="outline"
              className="flex-1 border-slate-600 text-white hover:bg-slate-800"
            >
              Close
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <>
      {/* Navigation overlay - shown while moving to question location */}
      {isNavigating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <Card className="bg-slate-900/95 border-slate-700 px-8 py-6 shadow-2xl">
            <div className="flex flex-col items-center gap-4 text-white">
              <div className="relative">
                <Navigation className="h-12 w-12 text-emerald-400 animate-pulse" />
              </div>
              <div className="text-center">
                <div className="font-bold text-xl">Moving to location...</div>
                <div className="text-sm text-slate-400 mt-1">
                  Navigating to question area
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Click capture overlay - ONLY visible when in listening mode */}
      {isListening && (
        <div 
          className="fixed inset-0 z-30 cursor-crosshair"
          onClick={(e) => {
            // Store screen position for visual marker FIRST
            const clickPos = {
              screenX: e.clientX,
              screenY: e.clientY
            };
            setClickedPosition(clickPos);
            // Then handle the click logic
            handleObjectClick();
          }}
          style={{ background: 'transparent' }}
          data-testid="interactive-click-overlay"
        />
      )}

      {/* Visual marker showing where user clicked - WITH CUSTOM HOTSPOT ICON */}
      {clickedPosition && clickedPosition.screenX && feedback && (
        <div 
          className="fixed pointer-events-none"
          style={{
            left: clickedPosition.screenX,
            top: clickedPosition.screenY,
            transform: 'translate(-50%, -50%)',
            zIndex: 9999
          }}
        >
          {/* Custom Hotspot icon marker */}
          <div className={`relative ${feedback.correct ? 'animate-bounce' : 'animate-pulse'}`}>
            {/* Outer ring animation */}
            <div 
              className={`absolute rounded-full ${feedback.correct ? 'bg-emerald-500' : 'bg-red-500'} animate-ping opacity-50`} 
              style={{ 
                width: 120, 
                height: 120, 
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)'
              }} 
            />
            
            {/* Second ring for more impact */}
            <div 
              className={`absolute rounded-full ${feedback.correct ? 'bg-emerald-400' : 'bg-red-400'} animate-pulse opacity-30`} 
              style={{ 
                width: 160, 
                height: 160, 
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                animationDelay: '0.2s'
              }} 
            />
            
            {/* Custom Hotspot Icon Image */}
            {feedback.correct ? (
              <div className="relative">
                {/* Glow effect behind icon */}
                <div 
                  className="absolute rounded-full bg-emerald-400 blur-xl opacity-60"
                  style={{ width: 80, height: 80, left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
                />
                <img 
                  src={customCorrectIcon || HOTSPOT_ICONS.correct}
                  alt="Correct!"
                  className="relative w-16 h-16 drop-shadow-2xl"
                  style={{ 
                    filter: 'drop-shadow(0 0 20px rgba(16, 185, 129, 0.8)) drop-shadow(0 0 40px rgba(16, 185, 129, 0.4))'
                  }}
                />
              </div>
            ) : (
              <div className={`relative w-14 h-14 rounded-full flex items-center justify-center shadow-2xl border-4 bg-gradient-to-br from-red-400 to-red-600 border-red-300`}>
                <XCircle className="h-8 w-8 text-white drop-shadow-lg" />
              </div>
            )}
            
            {/* Label below */}
            <div className={`absolute top-14 left-1/2 -translate-x-1/2 whitespace-nowrap px-3 py-1.5 rounded-full text-sm font-bold shadow-xl ${
              feedback.correct 
                ? 'bg-emerald-500 text-white' 
                : 'bg-red-500 text-white'
            }`}>
              {feedback.correct ? '✓ Correct!' : '✗ Wrong spot'}
            </div>
          </div>
        </div>
      )}

      {/* Fallback marker - shows in center of screen if no click position captured */}
      {!clickedPosition?.screenX && feedback && isInteractiveQuestion && (
        <div 
          className="fixed pointer-events-none"
          style={{
            left: '50%',
            top: '40%',
            transform: 'translate(-50%, -50%)',
            zIndex: 9999
          }}
        >
          <div className={`relative ${feedback.correct ? 'animate-bounce' : 'animate-pulse'}`}>
            <div 
              className={`absolute rounded-full ${feedback.correct ? 'bg-emerald-500' : 'bg-red-500'} animate-ping opacity-50`} 
              style={{ width: 140, height: 140, left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }} 
            />
            
            {feedback.correct ? (
              <div className="relative">
                <div 
                  className="absolute rounded-full bg-emerald-400 blur-xl opacity-60"
                  style={{ width: 100, height: 100, left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
                />
                <img 
                  src={customCorrectIcon || HOTSPOT_ICONS.correct}
                  alt="Correct!"
                  className="relative w-20 h-20 drop-shadow-2xl"
                  style={{ 
                    filter: 'drop-shadow(0 0 25px rgba(16, 185, 129, 0.8)) drop-shadow(0 0 50px rgba(16, 185, 129, 0.4))'
                  }}
                />
              </div>
            ) : (
              <div className={`relative w-16 h-16 rounded-full flex items-center justify-center shadow-2xl border-4 bg-gradient-to-br from-red-400 to-red-600 border-red-300`}>
                <XCircle className="h-9 w-9 text-white drop-shadow-lg" />
              </div>
            )}
            
            <div className={`absolute top-20 left-1/2 -translate-x-1/2 whitespace-nowrap px-4 py-2 rounded-full text-base font-bold shadow-xl ${
              feedback.correct ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
            }`}>
              {feedback.correct ? '✓ Correct!' : '✗ Wrong spot - Try Again'}
            </div>
          </div>
        </div>
      )}

      {/* BIG TIMER - Top right of screen */}
      {timeRemaining !== null && !result && (
        <div 
          className="fixed top-4 right-4 pointer-events-none"
          style={{ zIndex: 9998 }}
        >
          <div className={`px-6 py-4 rounded-2xl shadow-2xl backdrop-blur-xl border-2 ${
            timeRemaining < 60 
              ? 'bg-red-900/90 border-red-500 animate-pulse' 
              : timeRemaining < 180 
                ? 'bg-amber-900/90 border-amber-500' 
                : 'bg-slate-900/90 border-slate-600'
          }`}>
            <div className="flex items-center gap-3">
              <Clock className={`h-8 w-8 ${
                timeRemaining < 60 ? 'text-red-400' : timeRemaining < 180 ? 'text-amber-400' : 'text-slate-400'
              }`} />
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">Time Remaining</div>
                <div className={`text-4xl font-mono font-bold tracking-wider ${
                  timeRemaining < 60 ? 'text-red-400' : timeRemaining < 180 ? 'text-amber-400' : 'text-white'
                }`}>
                  {formatTime(timeRemaining)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="fixed inset-0 z-40 pointer-events-none">
        {/* Top instruction banner when listening - SIMPLIFIED */}
        {isListening && (
          <div className="absolute top-20 left-1/2 transform -translate-x-1/2 pointer-events-auto">
            <Card className="bg-emerald-600/95 backdrop-blur border-emerald-500 px-8 py-5 shadow-2xl">
              <div className="flex items-center gap-4 text-white">
                <div className="relative">
                  <Crosshair className="h-10 w-10 animate-pulse" />
                  <div className="absolute inset-0 rounded-full border-2 border-white/50 animate-ping" />
                </div>
                <div>
                  <div className="font-bold text-xl">Find: &quot;{currentQuestion?.correct_answer}&quot;</div>
                  <div className="text-sm text-emerald-100 mt-1">
                    Click on the object when you find it
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Question Card - Bottom Center - HIDDEN when listening */}
        {!isListening && (
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 pointer-events-auto max-w-lg w-full px-4">
          <Card className="bg-slate-900/95 backdrop-blur-xl border-slate-700 shadow-2xl overflow-hidden">
            {/* Header with progress */}
            <div className="px-4 py-3 bg-slate-800/50 border-b border-slate-700/50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="border-emerald-500/30 text-emerald-400">
                  Q{currentQuestionIndex + 1}/{quiz.questions.length}
                </Badge>
                <span className="text-sm text-slate-400">{quiz.title}</span>
              </div>
              {timeRemaining !== null && (
                <Badge 
                  variant="outline" 
                  className={`font-mono ${timeRemaining < 60 ? 'border-red-500/50 text-red-400 animate-pulse' : 'border-slate-600 text-slate-400'}`}
                >
                  <Clock className="h-3 w-3 mr-1" />
                  {formatTime(timeRemaining)}
                </Badge>
              )}
            </div>

            {/* Progress bar */}
            <Progress 
              value={((currentQuestionIndex + 1) / quiz.questions.length) * 100} 
              className="h-1 rounded-none"
            />

            {/* Question content */}
            <div className="p-5">
              {feedback ? (
                // Feedback display
                <div className={`p-4 rounded-lg ${feedback.correct ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                  <div className="flex items-start gap-3">
                    {feedback.correct ? (
                      <CheckCircle2 className="h-6 w-6 text-emerald-400 flex-shrink-0" />
                    ) : (
                      <XCircle className="h-6 w-6 text-red-400 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <div className={`font-semibold ${feedback.correct ? 'text-emerald-400' : 'text-red-400'}`}>
                        {feedback.correct ? 'Correct!' : 'Not quite...'}
                      </div>
                      <div className="text-sm text-slate-300 mt-1">{feedback.message}</div>
                      
                      {/* Show Try Again button if wrong and it's an interactive question */}
                      {!feedback.correct && isInteractiveQuestion && (
                        <Button 
                          onClick={() => {
                            setFeedback(null);
                            setIsListening(true);
                            setHoveredObject(null);
                            setClickedPosition(null); // Clear the visual marker
                          }}
                          className="mt-3 bg-amber-600 hover:bg-amber-500"
                          size="sm"
                        >
                          <RotateCcw className="h-4 w-4 mr-2" />
                          Try Again
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                // Question display
                <>
                  <h3 className="text-lg font-medium text-white mb-4">
                    {currentQuestion?.question}
                  </h3>

                  {isInteractiveQuestion ? (
                    // Interactive click question
                    <div className="space-y-4">
                      {!isListening ? (
                        <>
                          <div className="flex items-center gap-2 text-sm text-slate-400 mb-4">
                            <Target className="h-4 w-4 text-emerald-400" />
                            <span>Click the button below, then click on the correct object in the 3D view</span>
                          </div>
                          <Button 
                            onClick={() => setIsListening(true)}
                            className="w-full bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 py-6 text-lg"
                            data-testid="start-clicking-btn"
                          >
                            <Hand className="h-5 w-5 mr-2" />
                            Start Clicking in 3D View
                          </Button>
                          <p className="text-xs text-center text-slate-500">
                            You will be able to navigate and click on objects in the scene
                          </p>
                        </>
                      ) : (
                        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <Crosshair className="h-6 w-6 text-emerald-400 animate-pulse" />
                            </div>
                            <div className="flex-1">
                              <div className="text-emerald-400 font-medium">Click on the object now!</div>
                              <div className="text-sm text-slate-400 mt-1">
                                {hoveredObject 
                                  ? <>Hovering: <span className="text-white font-medium">{hoveredObject.name}</span></>
                                  : 'Move your cursor over objects in the 3D view'
                                }
                              </div>
                            </div>
                          </div>
                          <Button 
                            onClick={() => setIsListening(false)}
                            variant="outline"
                            className="w-full mt-4 border-slate-600 text-slate-300 hover:bg-slate-800"
                          >
                            Cancel
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : currentQuestion?.question_type === 'true_false' ? (
                    // True/False question
                    <div className="flex gap-3">
                      <Button
                        onClick={() => handleTrueFalseAnswer('true')}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-500 py-6 text-lg"
                      >
                        <CheckCircle2 className="h-5 w-5 mr-2" />
                        True
                      </Button>
                      <Button
                        onClick={() => handleTrueFalseAnswer('false')}
                        className="flex-1 bg-red-600 hover:bg-red-500 py-6 text-lg"
                      >
                        <XCircle className="h-5 w-5 mr-2" />
                        False
                      </Button>
                    </div>
                  ) : (
                    // Multiple choice question
                    <div className="space-y-2">
                      {currentQuestion?.options?.map((option, index) => (
                        <button
                          key={index}
                          onClick={() => handleMultipleChoiceAnswer(option)}
                          className="w-full text-left p-3 rounded-lg border border-slate-700 bg-slate-800/50 hover:bg-slate-700/50 hover:border-slate-600 transition-colors text-white"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-6 h-6 rounded-full border border-slate-600 flex items-center justify-center text-sm font-medium text-slate-400">
                              {String.fromCharCode(65 + index)}
                            </div>
                            <span>{option}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Score footer */}
            <div className="px-4 py-3 bg-slate-800/30 border-t border-slate-700/50 flex items-center justify-between text-sm">
              <div className="text-slate-400">
                Score: <span className="text-emerald-400 font-medium">{correctCount}</span>/{Object.keys(answers).length}
              </div>
              {!feedback && Object.keys(answers).length > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={submitQuizToBackend}
                  className="text-slate-400 hover:text-white"
                >
                  Submit Early
                </Button>
              )}
            </div>
          </Card>
        </div>
        )}
      </div>
    </>
  );
}

export default InteractiveQuizOverlay;
